import { useMutation } from "@apollo/client";
import { useState } from "react";
import {
  SIGN_TOS_MUTATION,
  SignTOSInput,
} from "globals/graphql/termsOfService.graphql";
import { useSnackbar } from "globals/hooks";
import { deriveStrictestCancellationPolicy } from "pages/new/utils/deriveStrictestCancellationPolicy";

export const useSignTermsOfService = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const snackbar = useSnackbar();

  const [signTOS] = useMutation(SIGN_TOS_MUTATION, {
    onError: (error) => {
      console.error("Error signing terms of service:", error);
      snackbar.error("Failed to sign terms and conditions. Please try again.");
      setIsSubmitting(false);
    },
  });

  const signTermsOfService = async (input: SignTOSInput) => {
    setIsSubmitting(true);

    try {
      // trips may not have a vehicle assigned yet; skip those rather than throw
      const selectedVehicleIds = input.request.trips
        .map((trip) => trip.routes?.[0]?.vehicle?.id)
        .filter((vehicleId): vehicleId is string => !!vehicleId);

      const cancellationPolicies = deriveStrictestCancellationPolicy({
        cancellationPoliciesData: input.cancellationPolicies,
        selectedVehicleIds,
      });

      const inputForMutation = {
        requestId: input.request.id,
        printedName: input.printedName,
        intro: input.intro,
        terms: input.terms,
        cancellationPolicies,
      };

      const response = await signTOS({
        variables: {
          input: inputForMutation,
        },
      });

      if (response.data?.signTOS?.success) {
        snackbar.success("Terms and conditions signed successfully!");
        return true;
      } else {
        snackbar.error(
          "Failed to sign terms and conditions. Please try again."
        );
        return false;
      }
    } catch (error) {
      console.error("Error signing terms of service:", error);
      snackbar.error("Failed to sign terms and conditions. Please try again.");
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    signTermsOfService,
    isSubmitting,
  };
};
